import { Suspense, lazy, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { Loader, Tabs } from '@nv/basic-components'
import { AdminEventDetailsHeader, BackgroundBanner, BottomBar, LoginTopBar, SideBar, TopBar } from '@nv/components'
import {
  AdminUserProvider,
  ConfigurationContext,
  LoginFlowContext,
  RegistrationUserProvider,
  SessionTimerProvider,
  VoterOnboardingContext,
  VoterUserProvider,
  useAdminUserContext,
  useConfigurationContext,
} from '@nv/contexts'
import { useAdminKeycloakAuth, useGetEvent, useVoterKeyCloakAuth } from '@nv/hooks'
import CalgaryBanner from '@nv/images/banners/calgary-banner.png'
import CalgaryLogo from '@nv/images/logos/calgary/calgary-logo'
import CalgaryLogoCollapsed from '@nv/images/logos/calgary/calgary-logo-collapsed'
import { AddElectors } from '@nv/images/nav-icons/add-electors'
import { Elections } from '@nv/images/nav-icons/elections'
import { ElectorsList } from '@nv/images/nav-icons/electors-list'
import { MyAccount } from '@nv/images/nav-icons/my-account'
import { Reports } from '@nv/images/nav-icons/reports'
import { Users } from '@nv/images/nav-icons/users'
import {
  AdminEditElector,
  AdminEditElectorRegistration,
  AdminEligibleDetails,
  AdminEventElectorsList,
  AdminEventOverview,
  AdminLogin,
  AdminMyAccount,
  AdminRegisteredOnlineList,
  AdminReports,
  AdminSFTP,
  AdminUserList,
  AdminVbmBarcodeScan,
  AdminVoterDetails,
  AuthAppSetup,
  AuthCodeMethod,
  AuthVerify,
  ChangePassword,
  ChangeQRCode,
  OnBoardingAuthMethod,
  OnBoardingCreatePassword,
  OnBoardingPhoneVerify,
  RegistrationStep3,
  ResetPassword,
  UserLogin,
  VoterEventDetails,
  VoterMyAccount,
} from '@nv/pages'
import { NvRoute, VerifyUserGuard } from '@nv/router'
import { nxConfig } from '@nv/utils'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes as Switch, useParams } from 'react-router-dom'
import getConfigurations from '../configurations'
import { AdminAddElector } from '../pages/admin/admin_add_elector/AdminAddElector'

// PUBLIC ROUTES:
const RegistrationLanding = lazy(() =>
  import('../pages/public/registration_landing/RegistrationLanding').then((mod: any) => ({ default: mod.RegistrationLanding })),
)
const RegistrationStep1Landing = lazy(() =>
  import('../pages/public/registration_step1_landing/RegistrationStep1Landing').then((mod: any) => ({
    default: mod.RegistrationStep1Landing,
  })),
)
const RegistrationStep2Landing = lazy(() =>
  import('../pages/public/registration_step2_landing/RegistrationStep2Landing').then((mod: any) => ({
    default: mod.RegistrationStep2Landing,
  })),
)

// VOTER ROUTES:
const VoterEventsLanding = lazy(() =>
  import('../pages/voter/voter_events_landing/VoterEventsLanding').then((mod: any) => ({ default: mod.VoterEventsLanding })),
)

// ADMINROUTES:
const AdminEventsLanding = lazy(() =>
  import('../pages/admin/admin_events_landing/AdminEventsLanding').then((mod: any) => ({ default: mod.AdminEventsLanding })),
)
const AdminElectorsListLanding = lazy(() =>
  import('../pages/admin/admin_eligible_list_landing/AdminEligibleListLanding').then((mod: any) => ({
    default: mod.AdminElectorsListLanding,
  })),
)

export const VoterKeyCloakAuth = () => {
  useVoterKeyCloakAuth()
  return null
}
export const AdminKeycloakAuth = () => {
  useAdminKeycloakAuth()
  return null
}

const VoterLayout = ({ children }) => {
  const { t } = useTranslation()
  const voterLinks = {
    elections: {
      path: '/voting/voter/dashboard',
      tabLabel: t('pages.events'),
      icon: Elections,
      hasPermissionToView: true,
    },
    // help: {
    //   path: '/voting/voter/help-support',
    //   tabLabel: t('pages.helpAndSupport'),
    //   icon: HelpAndSupportLogo,
    //   hasPermissionToView: true,
    // },
    myAccount: {
      path: '/voting/voter/my-account',
      tabLabel: t('pages.myAccount'),
      icon: MyAccount,
      hasPermissionToView: true,
    },
  }
  return (
    <>
      <SideBar links={voterLinks} logo={<CalgaryLogo />} collapsedLogo={<CalgaryLogoCollapsed />} />
      <div className='main-content-container'>
        <TopBar links={voterLinks} logo={<CalgaryLogo />} mobileLogo={<CalgaryLogoCollapsed />} />
        <main>{children}</main>
        <BottomBar links={voterLinks} />
      </div>
    </>
  )
}

const AdminLayout = ({ children, tabs }: any) => {
  const { t } = useTranslation()
  useAdminUserContext()

  const adminLinks = {
    events: {
      path: '/admin/dashboard',
      tabLabel: t('pages.events'),
      icon: Elections,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() ||
        KeycloakAdminService.isEventModuleAdmin() ||
        // KeycloakAdminService.isMonitoringAdmin() ||
        KeycloakAdminService.isVBMAdmin(),
    },
    electorsList: {
      path: '/admin/electors-list',
      tabLabel: t('pages.electorsList'),
      icon: ElectorsList,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() ||
        KeycloakAdminService.isEventModuleAdmin() ||
        KeycloakAdminService.isContactInfoRoleAdmin() ||
        KeycloakAdminService.isPollWorkerRoleAdmin() ||
        KeycloakAdminService.isVBMAdmin() ||
        KeycloakAdminService.isMonitoringAdmin(),
    },
    adminAddElectors: {
      path: '/admin/admin-add-electors',
      tabLabel: t('pages.addElectors'),
      icon: AddElectors,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() ||
        KeycloakAdminService.isContactInfoRoleAdmin() ||
        KeycloakAdminService.isVBMAdmin() ||
        KeycloakAdminService.isMonitoringAdmin(),
    },
    adminUsers: {
      path: '/admin/admin-users',
      tabLabel: t('pages.adminUsers'),
      icon: Users,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin(),
    },
    // HelpAndSupport: {
    //   path: '/admin/help-support',
    //   tabLabel: t('pages.helpAndSupport'),
    //   icon: HelpAndSupportLogo,
    //   hasPermissionToView: KeycloakAdminService.isSuperAdmin(),
    // },
    myAccount: {
      path: '/admin/my-account',
      tabLabel: t('pages.myAccount'),
      icon: MyAccount,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() ||
        KeycloakAdminService.isEventModuleAdmin() ||
        KeycloakAdminService.isMonitoringAdmin() ||
        KeycloakAdminService.isVBMAdmin(),
    },
    reports: {
      path: '/admin/reports',
      tabLabel: t('pages.reports'),
      icon: Reports,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin(),
    },
    sftp: {
      path: '/admin/sftp',
      tabLabel: t('pages.sftp'),
      icon: Reports,
      hasPermissionToView: KeycloakAdminService.hasRole('neuvote-super-admin'),
    },
  }

  return (
    <>
      <SideBar links={adminLinks} logo={<CalgaryLogo />} collapsedLogo={<CalgaryLogoCollapsed />} />
      <div className='main-content-container'>
        <TopBar links={adminLinks} logo={<CalgaryLogo />} mobileLogo={<CalgaryLogoCollapsed />} />
        {tabs}
        <main>{children}</main>
      </div>
    </>
  )
}

const AdminEventTabLayout = () => {
  const { eventId } = useParams()
  useAdminUserContext()
  const event = useGetEvent(eventId)
  const { t } = useTranslation()
  return (
    <>
      <AdminEventDetailsHeader eventId={eventId} />
      <Tabs
        tabs={[
          {
            label: t('events.labels.overview'),
            path: `/admin/dashboard/events-overview/${eventId}`,
          },
          {
            label: t('pages.electorsList'),
            path: `/admin/dashboard/electors-list/${eventId}/${event?.tiviId}`,
          },
        ]}
      />
    </>
  )
}

const AdmimElectorsListTabLayout = () => {
  const { t } = useTranslation()

  const tabs = [
    {
      label: t('admin.electorsList.electorsList'),
      path: `/admin/electors-list`,
    },
  ]
  if (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isMonitoringAdmin()) {
    tabs.push({
      label: t('registration.registeredOnline'),
      path: `/admin/electors-list/registered-online`,
    })
  }
  if (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) {
    // TODO: translation
    tabs.push({
      label: t('Scan Barcode'),
      path: `/admin/electors-list/vbm-barcode`,
    })
  }
  useAdminUserContext()
  return <Tabs tabs={tabs} />
}

const PublicLayout = ({ children }) => {
  const [authMethod, setAuthMethod] = useState(null)
  const reCaptchaKey = nxConfig.RECAPTCHA_SITE_KEY

  return (
    <div className='main-public-container'>
      <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
        <VoterOnboardingContext.Provider value={{ authMethod, setAuthMethod }}>
          <LoginTopBar logo={<CalgaryLogo />} />
          <BackgroundBanner banner={CalgaryBanner} />
          {children}
        </VoterOnboardingContext.Provider>
      </GoogleReCaptchaProvider>
    </div>
  )
}

const RoutesWrapper = () => {
  const [loginFlow, setLoginFlow] = useState('Login')
  const { t } = useTranslation()
  const configurations = getConfigurations({ t })

  return (
    <SessionTimerProvider>
      <VoterUserProvider>
        <AdminUserProvider>
          <LoginFlowContext.Provider value={{ loginFlow, setLoginFlow }}>
            <ConfigurationContext.Provider value={configurations}>
              <Routes />
            </ConfigurationContext.Provider>
          </LoginFlowContext.Provider>
        </AdminUserProvider>
      </VoterUserProvider>
    </SessionTimerProvider>
  )
}

const Routes = () => {
  // prettier-ignore
  const {includeOnlineVoterAuth} = useConfigurationContext()
  return (
    <>
      <Switch>
        <Route path={'/admin/*'} element={<AdminKeycloakAuth />} />
        <Route path='*' element={<VoterKeyCloakAuth />} />
      </Switch>

      <Suspense
        fallback={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
            <Loader />
          </div>
        }
      >
        <Switch>
          {/* ADMIN ROUTES */}
          <Route
            path='/admin/dashboard'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminEventsLanding} redirectURL={'/admin/login'} />}
                layout={AdminLayout}
              />
            }
          />
          <Route
            path='/admin/events'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminEventsLanding} redirectURL={'/admin/login'} />}
                layout={AdminLayout}
              />
            }
          />
          <Route
            path='/admin/electors-list'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminElectorsListLanding} redirectURL={'/admin/login'} />}
                layout={<AdminLayout tabs={<AdmimElectorsListTabLayout />} />}
              />
            }
          />
          <Route
            path='/admin/electors-list/registered-online'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminRegisteredOnlineList} redirectURL={'/admin/login'} />}
                layout={<AdminLayout tabs={<AdmimElectorsListTabLayout />} />}
              />
            }
          />
          <Route
            path='/admin/electors-list/vbm-barcode'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminVbmBarcodeScan} redirectURL={'/admin/login'} />}
                layout={<AdminLayout tabs={<AdmimElectorsListTabLayout />} />}
              />
            }
          />
          <Route
            path='/admin/registrant-details/:voterId'
            element={
              <NvRoute render={() => <VerifyUserGuard component={AdminVoterDetails} redirectURL={'/admin/login'} />} layout={AdminLayout} />
            }
          />
          <Route
            path='/admin/registrant-edit/:voterId'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminEditElectorRegistration} redirectURL={'/admin/login'} />}
                layout={AdminLayout}
              />
            }
          />
          <Route
            path='/admin/elector-edit/:voterId'
            element={
              <NvRoute render={() => <VerifyUserGuard component={AdminEditElector} redirectURL={'/admin/login'} />} layout={AdminLayout} />
            }
          />
          <Route
            path='/admin/elector-details/:voterId'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminEligibleDetails} redirectURL={'/admin/login'} />}
                layout={AdminLayout}
              />
            }
          />

          <Route
            path='/admin/admin-add-electors'
            element={
              <NvRoute render={() => <VerifyUserGuard component={AdminAddElector} redirectURL={'/admin/login'} />} layout={AdminLayout} />
            }
          />
          <Route
            path='/admin/admin-users'
            element={
              <NvRoute render={() => <VerifyUserGuard component={AdminUserList} redirectURL={'/admin/login'} />} layout={AdminLayout} />
            }
          />
          <Route
            path='/admin/my-account'
            element={
              <NvRoute render={() => <VerifyUserGuard component={AdminMyAccount} redirectURL={'/admin/login'} />} layout={AdminLayout} />
            }
          />
          {/* <Route
            path='/admin/help-support'
            element={
              <NvRoute render={() => <VerifyUserGuard component={HelpAndSupport} redirectURL={'/admin/login'} />} layout={AdminLayout} />
            }
          /> */}
          <Route
            path='/admin/sftp'
            element={<NvRoute render={() => <VerifyUserGuard component={AdminSFTP} redirectURL={'/admin/login'} />} layout={AdminLayout} />}
          />
          <Route
            path='/admin/change-password'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={() => <ChangePassword isAdmin={true} />} redirectURL={'/admin/login'} />}
                layout={AdminLayout}
              />
            }
          />
          <Route
            path='/admin/reports'
            element={
              <NvRoute render={() => <VerifyUserGuard component={AdminReports} redirectURL={'/admin/login'} />} layout={AdminLayout} />
            }
          />
          <Route
            path='/admin/dashboard/events-overview/:eventId'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminEventOverview} redirectURL={'/admin/login'} />}
                layout={<AdminLayout tabs={<AdminEventTabLayout />} />}
              />
            }
          />
          <Route
            path='/admin/dashboard/electors-list/:eventId/:tiviId'
            element={
              <NvRoute
                render={() => <VerifyUserGuard component={AdminEventElectorsList} redirectURL={'/admin/login'} />}
                layout={<AdminLayout tabs={<AdminEventTabLayout />} />}
              />
            }
          />

          {/* VOTER ROUTES */}
          <Route
            path='/voting/voter/dashboard'
            element={<NvRoute render={() => <VerifyUserGuard component={VoterEventsLanding} redirectURL={'/'} />} layout={VoterLayout} />}
          />
          <Route
            path='/voting/voter/my-account'
            element={<NvRoute render={() => <VerifyUserGuard component={VoterMyAccount} redirectURL={'/'} />} layout={VoterLayout} />}
          />
          {/* <Route
            path='/voting/voter/help-support'
            element={<NvRoute render={() => <VerifyUserGuard component={HelpAndSupport} redirectURL={'/'} />} layout={VoterLayout} />}
          /> */}
          <Route
            path='/voting/voter/change-qr-code'
            element={<NvRoute render={() => <VerifyUserGuard component={ChangeQRCode} redirectURL={'/'} />} layout={VoterLayout} />}
          />
          <Route
            path='/voting/voter/change-password'
            element={<NvRoute render={() => <VerifyUserGuard component={ChangePassword} redirectURL={'/'} />} layout={VoterLayout} />}
          />
          <Route
            path='/voting/voter/event-details/:eventId'
            element={<NvRoute render={() => <VerifyUserGuard component={VoterEventDetails} redirectURL={'/'} />} layout={VoterLayout} />}
          />

          {/* PUBLIC ROUTES */}
          {includeOnlineVoterAuth && (
            <>
              <Route path='/voting/voter/login' element={<NvRoute component={UserLogin} layout={PublicLayout} />} />
              <Route path='/voting/voter-login' element={<NvRoute component={UserLogin} layout={PublicLayout} />} />
            </>
          )}
          <Route path='/' element={<NvRoute component={RegistrationLanding} layout={PublicLayout} />} />
          <Route
            path='/registration/step1'
            element={
              <RegistrationUserProvider>
                <NvRoute component={RegistrationStep1Landing} layout={PublicLayout} />
              </RegistrationUserProvider>
            }
          />
          <Route
            path='/registration/step2'
            element={
              <RegistrationUserProvider>
                <NvRoute component={RegistrationStep2Landing} layout={PublicLayout} />
              </RegistrationUserProvider>
            }
          />
          <Route
            path='/registration/step3'
            element={
              <RegistrationUserProvider>
                <NvRoute component={RegistrationStep3} layout={PublicLayout} />
              </RegistrationUserProvider>
            }
          />
          {includeOnlineVoterAuth && (
            <>
              <Route path='/voting/voter-verify/select-verifier' element={<NvRoute component={AuthCodeMethod} layout={PublicLayout} />} />
              <Route path='/voting/voter-verify/verify' element={<NvRoute component={AuthVerify} layout={PublicLayout} />} />
              <Route path='/voting/voter-auth/reset-password' element={<NvRoute component={ResetPassword} layout={PublicLayout} />} />
              <Route
                path='/voting/voter-auth/create-password'
                element={<NvRoute component={OnBoardingCreatePassword} layout={PublicLayout} />}
              />
              <Route
                path='/voting/voter-onboarding/phone-verify'
                element={<NvRoute component={OnBoardingPhoneVerify} layout={PublicLayout} />}
              />
              <Route
                path='/voting/voter-onboarding/auth-method'
                element={<NvRoute component={OnBoardingAuthMethod} layout={PublicLayout} />}
              />
              <Route path='/voting/voter-auth/auth-app-setup' element={<NvRoute component={AuthAppSetup} layout={PublicLayout} />} />
              <Route path='/voting/voter-auth/change-password' element={<NvRoute component={ChangePassword} layout={PublicLayout} />} />
            </>
          )}
          <Route path='/admin' element={<NvRoute component={AdminLogin} layout={PublicLayout} />} />
          <Route path='/admin/login' element={<NvRoute component={AdminLogin} layout={PublicLayout} />} />
          <Route path='/admin/auth/verify' element={<NvRoute component={AuthVerify} layout={PublicLayout} />} />
          <Route path='/admin/auth/auth-app-setup' element={<NvRoute component={AuthAppSetup} layout={PublicLayout} />} />
          <Route
            path='/admin/auth/change-password'
            element={<NvRoute component={() => <ChangePassword isAdmin={true} />} layout={PublicLayout} />}
          />
          <Route path='/admin/auth/reset-password' element={<NvRoute component={ResetPassword} layout={PublicLayout} />} />

          <Route path='/voting/voter/*' element={<NvRoute render={() => <Navigate to='/voting/voter/dashboard' />} />} />
          <Route path='/admin/*' element={<NvRoute render={() => <Navigate to='/admin/dashboard' />} />} />
          <Route path='*' element={<Navigate to='/' />} />
        </Switch>
      </Suspense>
    </>
  )
}
export { RoutesWrapper as Routes }
